import type { FunctionMeta } from '../function.model';

// =============================================================================
// PARAMS - Mathematical definition
// =============================================================================

/**
 * Quadratic function parameters supporting both representations:
 * - Vertex form: a(x-h)² + k
 * - Standard form: ax² + bx + c
 *
 * Both representations are stored and kept in sync bidirectionally.
 * The vertex form (a, h, k) is used internally for evaluation and handle positioning.
 */
export interface QuadraticParams {
  /** Vertical stretch/compression and reflection (shared by both forms) */
  a: number;
  /** Horizontal shift - vertex x-coordinate (vertex form) */
  h: number;
  /** Vertical shift - vertex y-coordinate (vertex form) */
  k: number;
  /** Linear coefficient (standard form) */
  b: number;
  /** Constant term (standard form) */
  c: number;
}

// =============================================================================
// CONVERSION UTILITIES
// =============================================================================

/**
 * Convert vertex form parameters (a, h, k) to standard form (b, c).
 *
 * Derivation:
 *   a(x-h)² + k = a(x² - 2hx + h²) + k = ax² - 2ahx + ah² + k
 *   Comparing with ax² + bx + c:
 *   - b = -2ah
 *   - c = ah² + k
 */
export function vertexToStandard(a: number, h: number, k: number): { b: number; c: number } {
  return {
    b: -2 * a * h,
    c: a * h * h + k,
  };
}

/**
 * Convert standard form parameters (a, b, c) to vertex form (h, k).
 *
 * Derivation:
 *   From b = -2ah → h = -b / (2a)
 *   From c = ah² + k → k = c - ah² = c - a(b/(2a))² = c - b²/(4a)
 */
export function standardToVertex(a: number, b: number, c: number): { h: number; k: number } {
  const h = -b / (2 * a);
  const k = c - (b * b) / (4 * a);
  return { h, k };
}

// =============================================================================
// META - Function configuration (type-checked)
// =============================================================================

/**
 * Configuration metadata for quadratic functions.
 * All translate behaviors map to h (x) and k (y) parameters.
 */
export const QUADRATIC_META: FunctionMeta<QuadraticParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'extrema',
    'tangent',
    'secant',
    'derivative',
    'axisOfSymmetry',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
  ],
  handleConfigs: {
    translate: { xParam: 'h', yParam: 'k' },
    'translate-x': { xParam: 'h', yParam: 'k' },
    'translate-y': { xParam: 'h', yParam: 'k' },
  },
  defaultParams: {
    a: 1,
    h: 0,
    k: 0,
    b: 0, // -2 * 1 * 0 = 0
    c: 0, // 1 * 0² + 0 = 0
  },
  defaultRanges: {
    a: [-5, 5],
    h: [-10, 10],
    k: [-10, 10],
    b: [-20, 20],
    c: [-20, 20],
  },
};
